// Боковая панель: разделы, чаты по дням, документы по папкам, аккаунт.
import { useRef, useState } from "react";
import { boot } from "../boot";
import { chatGroup, shortDate } from "../lib/text";
import { navigate, routeStore } from "../router";
import {
  chatsStore, createFolder, DOC_DRAG, currentChatStore, docName, downloadMany, foldersStore, itemMeta, moveToFolder, openCalc, openLogin, prefetchChat, removeChat,
  removeFolder, removeSaved, removeUploaded, renameChat, renameEntry, renameFolder, savedStore, searchQuery, searchStore, uploadedStore, useStore,
  type ChatBrief, type Folder, type FolderEntry, type SavedItem,
} from "../store";
import { ActionMenu, FolderNameField, type MenuAction } from "./ActionMenu";
import { Icon } from "./Icon";
import { useTabs } from "./Tabs";
import { openViewer, viewerStore, type ViewerTarget } from "./Viewer";
import { logout } from "../lib/account";

export const NAV_ACTIVE: Partial<Record<string, string>> = { my: "my", documents: "documents" };

function ChatMenu({ chat, onRename }: { chat: ChatBrief; onRename: () => void }) {
  return (
    <ActionMenu label={`Действия с чатом «${chat.title}»`} actions={[
      { label: "Переименовать", icon: "edit", run: onRename },
      { label: "Удалить", icon: "trash", danger: true, run: () => removeChat(chat) },
    ]} />
  );
}

// Название чата правится прямо в списке: Enter — сохранить, Escape — отменить.
function RenameField({ chat, onDone }: { chat: ChatBrief; onDone: () => void }) {
  const [value, setValue] = useState(chat.title);
  const done = useRef(false);
  const save = () => {
    if (done.current) return;
    done.current = true;
    const title = value.trim();
    if (title && title !== chat.title) renameChat(chat, title);
    onDone();
  };
  return (
    <input className="saved-rename" value={value} autoFocus maxLength={80} aria-label="Новое название чата"
      onFocus={(event) => event.currentTarget.select()} onChange={(event) => setValue(event.target.value)} onBlur={save}
      onKeyDown={(event) => {
        if (event.key === "Enter") { event.preventDefault(); save(); }
        if (event.key === "Escape") { done.current = true; onDone(); }
      }} />
  );
}

function ChatList() {
  const [editing, setEditing] = useState<string | null>(null);
  const chats = useStore(chatsStore);
  const current = useStore(currentChatStore);
  useStore(searchStore);
  const query = searchQuery();
  const found = chats.filter((chat) => chat.title.toLocaleLowerCase("ru").includes(query));
  const groups = new Map<string, ChatBrief[]>();
  for (const chat of found) {
    const name = chatGroup(chat.updated_at);
    groups.set(name, [...(groups.get(name) || []), chat]);
  }
  return (
    <>
      <div className="chat-groups">
        {[...groups].map(([name, items]) => (
          <div key={name} className="chat-group">
            <span className="chat-group-title">{name}</span>
            <ul className="saved-list">
              {items.map((chat) => (
                <li key={chat.id}>
                  {editing === chat.id ? <RenameField chat={chat} onDone={() => setEditing(null)} /> : (
                    <a className={`saved-item${chat.id === current ? " is-current" : ""}`} href={`/?chat=${chat.id}`} title={chat.title}
                      aria-current={chat.id === current ? "page" : undefined}
                      onPointerEnter={() => prefetchChat(chat.id)} onTouchStart={() => prefetchChat(chat.id)} onFocus={() => prefetchChat(chat.id)}>
                      <span className="saved-title">{chat.title}</span>
                    </a>
                  )}
                  <ChatMenu chat={chat} onRename={() => setEditing(chat.id)} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="chats-empty" hidden={chats.length > 0}>
        <span className="chats-empty-icon"><Icon name="chat" /></span>
        <strong>Начните новый чат</strong>
        <ul>
          <li><Icon name="check" />Задайте вопрос по документу</li>
          <li><Icon name="check" />Попросите составить иск или заявление</li>
          <li><Icon name="check" />История чатов сохранится здесь</li>
        </ul>
      </div>
      <p className="saved-empty" hidden={!(chats.length && !found.length)}>Ничего не найдено. Попробуйте другие слова.</p>
    </>
  );
}

const DRAG_TYPE = DOC_DRAG;
const OPEN_KEY = "pravosvod.openFolders";
type Entry = FolderEntry & { key: string; date: string; title: string; meta: string };

function readOpen(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) || "[]")); } catch { return new Set(); }
}

// Документ по щелчку открывается справа (просмотр); расчёт — в чате. Разобрать и скачать — в меню «⋯».
function EntryRow({ entry, folders, dragged, onDrag }: { entry: Entry; folders: Folder[]; dragged: boolean; onDrag: (key: string | null) => void }) {
  const current = entry.item.folder_id || null;
  const [renaming, setRenaming] = useState(false);
  const viewer = useStore(viewerStore);
  const target: ViewerTarget | null = entry.kind === "upload" ? { kind: "upload", id: entry.item.id }
    : entry.item.kind === "doc" ? { kind: "saved", id: entry.item.id } : null;
  const open = Boolean(target && viewer.target?.kind === target.kind && viewer.target.id === target.id);
  const actions: MenuAction[] = [
    ...(entry.kind === "upload" ? [{ label: "Разобрать документ", icon: "search", run: () => navigate(`/?start=review:${entry.item.id}`) }] : []),
    ...(target ? [{ label: entry.kind === "upload" ? "Скачать" : "Скачать Word", icon: "download", run: () => downloadMany([entry]) }] : []),
    { label: "Переименовать", icon: "edit", run: () => setRenaming(true) },
    ...folders.filter((folder) => folder.id !== current)
      .map((folder) => ({ label: `В папку «${folder.name}»`, icon: "folder", run: () => moveToFolder(entry, folder.id) })),
    ...(current ? [{ label: "Убрать из папки", icon: "arrow", run: () => moveToFolder(entry, null) }] : []),
    { label: "Удалить", icon: "trash", danger: true,
      run: () => (entry.kind === "saved" ? removeSaved(entry.item) : removeUploaded(entry.item)) },
  ];
  return (
    <li draggable className={dragged ? "is-dragging" : undefined}
      onDragStart={(event) => { event.dataTransfer.setData(DRAG_TYPE, entry.key); event.dataTransfer.effectAllowed = "move"; onDrag(entry.key); }}
      onDragEnd={() => onDrag(null)}>
      {renaming ? (
        <FolderNameField initial={entry.title} label="Новое название документа" placeholder="Название документа" maxLength={120}
          onSave={(name) => renameEntry(entry, name)} onDone={() => setRenaming(false)} />
      ) : target ? (
        <button type="button" className={`saved-item${open ? " is-current" : ""}`} aria-pressed={open} title="Открыть справа"
          onClick={() => openViewer(target)}>
          <span className="saved-title">{entry.title}</span>
          <span className="saved-meta">{entry.meta}</span>
        </button>
      ) : (
        <button type="button" className="saved-item" title="Показать расчёт" onClick={() => openCalc(entry.item as SavedItem)}>
          <span className="saved-title">{entry.title}</span>
          <span className="saved-meta">{entry.meta}</span>
        </button>
      )}
      <ActionMenu label={`Действия с «${entry.title}»`} actions={actions} />
    </li>
  );
}

// Документы, расчёты и загруженные файлы: сверху папки, ниже — всё, что не разложено, свежие сверху.
// Документ перетаскивают в папку мышью; на телефоне и с клавиатуры — через меню «⋯».
function DocList() {
  const saved = useStore(savedStore);
  const uploaded = useStore(uploadedStore);
  const folders = useStore(foldersStore);
  useStore(searchStore);
  const query = searchQuery();
  const [open, setOpen] = useState(readOpen);
  const [editing, setEditing] = useState<string | null>(null);     // id папки или "new"
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);           // id папки или "root"

  const entries: Entry[] = [
    ...[...saved.doc, ...saved.calc].map((item): Entry => ({ kind: "saved", item, key: `s${item.id}`, date: item.created_at, title: item.title, meta: itemMeta(item) })),
    ...uploaded.map((doc): Entry => ({ kind: "upload", item: doc, key: `u${doc.id}`, date: doc.created_at, title: docName(doc), meta: `Загружен · ${shortDate(doc.created_at)}` })),
  ].sort((a, b) => b.date.localeCompare(a.date));
  const known = new Set(folders.map((folder) => folder.id));
  const folderOf = (entry: Entry) => (entry.item.folder_id && known.has(entry.item.folder_id) ? entry.item.folder_id : null);
  const matches = (entry: Entry) => `${entry.title} ${entry.meta} ${entry.kind === "upload" ? entry.item.filename : ""}`.toLocaleLowerCase("ru").includes(query);
  const root = entries.filter((entry) => !folderOf(entry) && matches(entry));

  const toggle = (id: string, value?: boolean) => setOpen((before) => {
    const next = new Set(before);
    if (value ?? !next.has(id)) next.add(id); else next.delete(id);
    try { localStorage.setItem(OPEN_KEY, JSON.stringify([...next])); } catch { /* необязательная настройка */ }
    return next;
  });
  const dropZone = (target: string, folderId: string | null) => ({
    onDragOver: (event: React.DragEvent) => {
      if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (over !== target) setOver(target);
    },
    onDragLeave: (event: React.DragEvent) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOver(null); },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setOver(null);
      setDragged(null);
      const entry = entries.find((one) => one.key === event.dataTransfer.getData(DRAG_TYPE));
      if (entry) moveToFolder(entry, folderId);
    },
  });
  const row = (entry: Entry) => <EntryRow key={entry.key} entry={entry} folders={folders} dragged={dragged === entry.key} onDrag={setDragged} />;

  const shownFolders = folders.map((folder) => {
    const inside = entries.filter((entry) => folderOf(entry) === folder.id);
    const nameMatches = folder.name.toLocaleLowerCase("ru").includes(query);
    const items = query && !nameMatches ? inside.filter(matches) : inside;
    return { folder, inside, items, visible: !query || nameMatches || items.length > 0 };
  }).filter((one) => one.visible);
  const nothing = !root.length && !shownFolders.length;

  return (
    <>
      <div className="folder-bar">
        <button type="button" className="folder-new" onClick={() => setEditing("new")}><Icon name="plus" />Новая папка</button>
      </div>
      {editing === "new" && (
        <div className="folder-row is-editing">
          <FolderNameField initial="" label="Название новой папки" onDone={() => setEditing(null)}
            onSave={async (name) => { const folder = await createFolder(name); if (folder) toggle(folder.id, true); }} />
        </div>
      )}
      {shownFolders.length > 0 && (
        <ul className="folder-list">
          {shownFolders.map(({ folder, inside, items }) => {
            const expanded = open.has(folder.id) || (Boolean(query) && items.length > 0);
            return (
              <li key={folder.id} className={`folder${over === folder.id ? " is-over" : ""}`} {...dropZone(folder.id, folder.id)}>
                <div className="folder-row">
                  {editing === folder.id ? (
                    <FolderNameField initial={folder.name} label="Новое название папки" onSave={(name) => renameFolder(folder, name)} onDone={() => setEditing(null)} />
                  ) : (
                    <button type="button" className="folder-toggle" aria-expanded={expanded} onClick={() => toggle(folder.id)}>
                      <span className="folder-chevron"><Icon name="chevron" /></span>
                      <Icon name="folder" />
                      <span className="folder-name">{folder.name}</span>
                      <span className="folder-count" aria-label={`документов: ${inside.length}`}>{inside.length}</span>
                    </button>
                  )}
                  <ActionMenu label={`Действия с папкой «${folder.name}»`} actions={[
                    { label: "Переименовать", icon: "edit", run: () => setEditing(folder.id) },
                    { label: "Удалить папку", icon: "trash", danger: true, run: () => removeFolder(folder) },
                  ]} />
                </div>
                {expanded && (items.length
                  ? <ul className="saved-list folder-items">{items.map(row)}</ul>
                  : <p className="folder-empty">Перетащите сюда документ или выберите «В папку» в его меню «⋯».</p>)}
              </li>
            );
          })}
        </ul>
      )}
      <ul className={`saved-list folder-root${over === "root" ? " is-over" : ""}${dragged ? " is-target" : ""}`} {...dropZone("root", null)}
        aria-label="Документы без папки">
        {root.slice(0, 30).map(row)}
        {dragged && folders.length > 0 && <li className="folder-root-hint">Отпустите здесь, чтобы убрать из папки</li>}
      </ul>
      <p className="saved-empty" hidden={!nothing}>
        {query ? "Ничего не найдено. Попробуйте другое название." : "Здесь появятся готовые документы, расчёты и загруженные файлы."}
      </p>
    </>
  );
}

export function Sidebar({ onClose, closeRef }: { onClose: () => void; closeRef: React.Ref<HTMLButtonElement> }) {
  const { selected, tab } = useTabs(["chats", "docs"] as const, "chats");
  const search = useStore(searchStore);
  const active = NAV_ACTIVE[useStore(routeStore).page];
  const user = boot.user;
  return (
    <aside className="sidebar" id="sidebar">
      <div className="sidebar-top">
        <a className="brand" href="/" aria-label="Правосвод">
          <img className="brand-mark" src={`/static/brand/mark-64.png?v=${boot.version}`} alt="" width="22" height="22" />
          <span className="brand-name" aria-hidden="true">Право<span>свод</span></span>
        </a>
        <button ref={closeRef} className="icon-button sidebar-toggle" type="button" aria-label="Свернуть меню" title="Свернуть меню" onClick={onClose}>
          <Icon name="panel" />
        </button>
      </div>

      <nav className="nav" aria-label="Разделы">
        <a href="/" className="nav-new"><Icon name="edit" />Новый чат</a>
        <a href="/my" className={active === "my" ? "active" : undefined} aria-current={active === "my" ? "page" : undefined}><Icon name="library" />Мои документы</a>
        <a href="/documents" className={active === "documents" ? "active" : undefined} aria-current={active === "documents" ? "page" : undefined}><Icon name="book" />Шаблоны документов</a>
      </nav>

      <section className="saved" id="saved" aria-label="Чаты и документы">
        <div className="saved-tabs" role="tablist" aria-label="Чаты и документы">
          <button {...tab("chats")} id="saved-tab-chats" aria-controls="saved-chats">Чаты</button>
          <button {...tab("docs")} id="saved-tab-docs" aria-controls="saved-docs">Документы</button>
        </div>
        <label className="saved-search">
          <Icon name="search" />
          <input type="search" value={search} onChange={(event) => searchStore.set(event.target.value)}
            placeholder={selected === "chats" ? "Поиск чатов…" : "Поиск документов…"} aria-label="Поиск по чатам и документам" />
        </label>
        <div data-saved-panel="chats" id="saved-chats" role="tabpanel" aria-labelledby="saved-tab-chats" hidden={selected !== "chats"}><ChatList /></div>
        <div data-saved-panel="docs" id="saved-docs" role="tabpanel" aria-labelledby="saved-tab-docs" hidden={selected !== "docs"}><DocList /></div>
      </section>

      <div className="account">
        <span className="account-avatar" aria-hidden="true">{user ? (user.name || user.email || "?").slice(0, 1).toUpperCase() : <Icon name="user" />}</span>
        <span className="account-text">
          <span className="account-name">{user ? user.name || user.email : "Гость"}</span>
          <span className="account-hint">{user ? user.email : "Тестовая версия"}</span>
        </span>
        {!user && (
          <button type="button" className="icon-button" title="Войти или зарегистрироваться" aria-label="Войти или зарегистрироваться" onClick={() => openLogin()}>
            <Icon name="login" />
          </button>
        )}
        <ActionMenu label="Меню аккаунта" title="Аккаунт" className="saved-more account-more" above actions={[
          ...(boot.isAdmin ? [{ label: "Администратор", icon: "shield", run: () => navigate("/admin") }] : []),
          { label: "Дашборд", icon: "grid", run: () => navigate("/dashboard") },
          { label: "Настройки", icon: "settings", run: () => navigate("/settings") },
          { label: "Политика конфиденциальности", icon: "shield", run: () => navigate("/privacy") },
          ...(user ? [{ label: "Выйти из аккаунта", icon: "logout", danger: true, divider: true, run: logout }] : []),
        ]} />
      </div>
    </aside>
  );
}
